import axios from "axios";
import { cacheResponse, cacheFallback } from "@/lib/apiCache";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stable id for this browser tab so the realtime layer can ignore the echo of
// our own writes (the backend tags each change with the originating client id).
export const CLIENT_ID = `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const api = axios.create({ baseURL: API, withCredentials: true });
api.defaults.headers.common["X-Client-Id"] = CLIENT_ID;

let refreshing = null;
api.interceptors.response.use(
  (r) => cacheResponse(r),
  async (error) => {
    const cfg = error.config;
    const url = cfg?.url || "";
    if (error.response?.status === 401 && !cfg._retry && !url.includes("/auth/")) {
      cfg._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(cfg);
      } catch (e) {
        refreshing = null;
        window.dispatchEvent(new Event("auth:expired"));
      }
    }
    // Offline: fall back to the last cached value for GET requests we've seen before.
    const cached = await cacheFallback(error);
    if (cached) return cached;
    return Promise.reject(error);
  }
);

export function formatError(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export async function downloadFile(path, params, fallbackName) {
  const res = await api.get(path, { params, responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const name = match ? match[1] : fallbackName;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const tablePath = (dbId, table) => `/databases/${dbId}/tables/${encodeURIComponent(table)}`;
